<template>
  <v-container fluid class="mt-10 px-8">
    <!-- Page header — always visible in both form and success states -->
    <v-row>
      <v-col>
        <div class="d-flex align-center mb-1">
          <v-btn icon small @click="goToList" class="mr-2" aria-label="Back to list">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <h1 class="font-weight-thin">{{ createdReleaseNote ? "Release Note Created" : "Review &amp; Import" }}</h1>
        </div>
        <div v-if="issue.key" class="ml-10 mb-1">
          <a :href="`https://jira.ochin.org/browse/${issue.key}`" target="_blank" rel="noopener noreferrer" title="Opens in new tab"><strong>{{ issue.key }}</strong></a>
          <span class="grey--text text--darken-2"> — {{ issue.fields && issue.fields.summary }}</span>
        </div>
        <p v-if="!createdReleaseNote" class="body-2 grey--text text--darken-2 ml-10 mb-0">
          Review the Jira data on the left, then confirm the Continuum fields on the right.
        </p>
      </v-col>
    </v-row>

    <!-- Success state -->
    <template v-if="createdReleaseNote">
      <v-row class="mt-4" justify="center">
        <v-col cols="12" md="9" lg="7">
          <v-card outlined>
            <!-- Success banner -->
            <div class="success-banner">
              <v-icon color="success" class="mr-2" aria-hidden="true">check_circle</v-icon>
              <span class="subtitle-2">Release note created successfully</span>
            </div>

            <v-card-text class="pt-4">
              <!-- Unmapped analysts -->
              <template v-if="hasUnmappedFields">
                <div class="manual-assignment-section pa-3" role="alert">
                  <div class="d-flex align-center mb-2">
                    <v-icon small color="warning" class="mr-2" aria-hidden="true">warning</v-icon>
                    <span class="subtitle-2">Requires Manual Assignment</span>
                  </div>
                  <div class="body-2 grey--text text--darken-2 mb-3">
                    The following analyst{{ hasMultipleUnmapped ? "s" : "" }} could not be auto-mapped from Jira. Open the release note to assign them manually.
                  </div>
                  <div v-if="issue.resolvedAssignee && !issue.resolvedAssignee.found" class="mt-2">
                    <div class="caption grey--text text--darken-2">QA Analyst</div>
                    <div>{{ issue.resolvedAssignee.displayName }}</div>
                  </div>
                  <div v-if="issue.resolvedReporter && !issue.resolvedReporter.found" class="mt-2">
                    <div class="caption grey--text text--darken-2">Build Analyst</div>
                    <div>{{ issue.resolvedReporter.displayName }}</div>
                  </div>
                  <div v-if="unmappedWatchers.length" class="mt-2">
                    <div class="caption grey--text text--darken-2">Support Analysts</div>
                    <div>{{ unmappedWatchers.map(w => w.displayName).join(", ") }}</div>
                  </div>
                </div>
              </template>

              <!-- Server-side warnings when the manual-assignment section is not shown -->
              <template v-if="!hasUnmappedFields && postCreateWarnings.length">
                <div class="inline-alert inline-alert--warning body-2">
                  <div v-for="warning in postCreateWarnings" :key="warning" class="mb-1">{{ warning }}</div>
                </div>
              </template>
            </v-card-text>

            <v-card-actions class="pa-4 pt-2">
              <v-spacer></v-spacer>
              <v-btn @click="goToList" text data-cy="jira-qa-import-create__back">Back to List</v-btn>
              <v-btn @click="goToReleaseNote" color="primary" depressed data-cy="jira-qa-import-create__view">View Release Note</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Import form -->
    <template v-else>
      <v-row class="mt-4" align="stretch">
        <!-- Left panel: From Jira (read-only) -->
        <v-col cols="12" md="5" class="d-flex">
          <v-card outlined class="flex-grow-1">
            <v-card-title class="subtitle-1 font-weight-medium pb-1">From Jira</v-card-title>
            <v-card-text>
              <!-- Auto-mapped release note fields -->
              <div class="overline grey--text text--darken-2 mb-2">Release Note Details</div>
              <div v-if="singleSelectValue(issue.fields && issue.fields.customfield_12314)" class="jira-field__row">
                <span class="jira-field__label">Build Type</span>
                <span>{{ singleSelectValue(issue.fields && issue.fields.customfield_12314) }}</span>
              </div>
              <div v-if="singleSelectValue(issue.fields && issue.fields.customfield_12315)" class="jira-field__row">
                <span class="jira-field__label">Release Type</span>
                <span>{{ singleSelectValue(issue.fields && issue.fields.customfield_12315) }}</span>
              </div>
              <div class="jira-field__row">
                <span class="jira-field__label">UAT Environment</span>
                <span v-if="multiSelectValue(issue.fields && issue.fields.customfield_12316)">{{ multiSelectValue(issue.fields && issue.fields.customfield_12316) }}</span>
                <span v-else class="grey--text text--darken-2">—</span>
              </div>
              <div v-if="singleSelectValue(issue.fields && issue.fields.customfield_16003)" class="jira-field__row">
                <span class="jira-field__label">Operationalize Type</span>
                <span>{{ singleSelectValue(issue.fields && issue.fields.customfield_16003) }}</span>
              </div>
              <div v-if="singleSelectValue(issue.fields && issue.fields.customfield_20501)" class="jira-field__row">
                <span class="jira-field__label">Care Setting</span>
                <span>{{ singleSelectValue(issue.fields && issue.fields.customfield_20501) }}</span>
              </div>
              <div v-if="singleSelectValue(issue.fields && issue.fields.customfield_18700)" class="jira-field__row">
                <span class="jira-field__label">Other Activities Impacted</span>
                <span>{{ singleSelectValue(issue.fields && issue.fields.customfield_18700) }}</span>
              </div>

              <!-- Application & Primary Activity section -->
              <template v-if="jiraApplicationLabel || jiraPrimaryActivityLabel">
                <v-divider class="mt-1 mb-3"></v-divider>
                <div class="overline grey--text text--darken-2 mb-2">Application &amp; Primary Activity</div>
                <div v-if="jiraApplicationLabel" class="jira-field__row">
                  <span class="jira-field__label">Application</span>
                  <v-tooltip v-if="isInitialized && suggestedApplicationId" bottom>
                    <template v-slot:activator="{ on }"><v-icon x-small color="success" class="mr-1" v-on="on" aria-hidden="true">check_circle</v-icon></template>
                    <span>Matched — pre-filled on the right</span>
                  </v-tooltip>
                  <v-tooltip v-else-if="isInitialized" bottom>
                    <template v-slot:activator="{ on }"><v-icon x-small color="warning" class="mr-1" v-on="on" aria-hidden="true">warning</v-icon></template>
                    <span>No match found — select manually on the right</span>
                  </v-tooltip>
                  <span>{{ jiraApplicationLabel }}</span>
                </div>
                <div v-if="jiraPrimaryActivityLabel" class="jira-field__row">
                  <span class="jira-field__label">Primary Activity</span>
                  <v-tooltip v-if="isInitialized && suggestedPrimaryActivityId" bottom>
                    <template v-slot:activator="{ on }"><v-icon x-small color="success" class="mr-1" v-on="on" aria-hidden="true">check_circle</v-icon></template>
                    <span>Matched — pre-filled on the right</span>
                  </v-tooltip>
                  <v-tooltip v-else-if="isInitialized" bottom>
                    <template v-slot:activator="{ on }"><v-icon x-small color="warning" class="mr-1" v-on="on" aria-hidden="true">warning</v-icon></template>
                    <span>No match found — select manually on the right</span>
                  </v-tooltip>
                  <span>{{ jiraPrimaryActivityLabel }}</span>
                </div>
              </template>

              <!-- Service Area Attributes section -->
              <v-divider class="mt-1 mb-3"></v-divider>
              <div class="overline grey--text text--darken-2 mb-2">Service Area Attributes</div>
              <div class="jira-field__row">
                <v-tooltip v-if="isInitialized && parsedJiraServiceAreaPairs.length && suggestedServiceAreaAttributeIds.length === parsedJiraServiceAreaPairs.length" bottom>
                  <template v-slot:activator="{ on }"><v-icon x-small color="success" class="mr-1" v-on="on" aria-hidden="true">check_circle</v-icon></template>
                  <span>Matched — pre-filled on the right</span>
                </v-tooltip>
                <v-tooltip v-else-if="isInitialized && parsedJiraServiceAreaPairs.length" bottom>
                  <template v-slot:activator="{ on }"><v-icon x-small color="warning" class="mr-1" v-on="on" aria-hidden="true">warning</v-icon></template>
                  <span>One or more values could not be matched — review on the right</span>
                </v-tooltip>
                <span v-if="jiraServiceAreaLabel">{{ jiraServiceAreaLabel }}</span>
                <span v-else class="grey--text text--darken-2">—</span>
              </div>

              <!-- Impacted User Roles section -->
              <v-divider class="mt-1 mb-3"></v-divider>
              <div class="overline grey--text text--darken-2 mb-2">Impacted User Roles</div>
              <div class="jira-field__row">
                <v-tooltip v-if="isInitialized && jiraImpactedUserRolesRawValues.length && suggestedUserRoles.length === jiraImpactedUserRolesUniqueRawCount" bottom>
                  <template v-slot:activator="{ on }"><v-icon x-small color="success" class="mr-1" v-on="on" aria-hidden="true">check_circle</v-icon></template>
                  <span>Matched — pre-filled on the right</span>
                </v-tooltip>
                <v-tooltip v-else-if="isInitialized && jiraImpactedUserRolesRawValues.length" bottom>
                  <template v-slot:activator="{ on }"><v-icon x-small color="warning" class="mr-1" v-on="on" aria-hidden="true">warning</v-icon></template>
                  <span>One or more values could not be matched — review on the right</span>
                </v-tooltip>
                <span v-if="jiraImpactedUserRolesLabel">{{ jiraImpactedUserRolesLabel }}</span>
                <span v-else class="grey--text text--darken-2">—</span>
              </div>

              <!-- Analysts section -->
              <v-divider class="mt-1 mb-3"></v-divider>
              <div class="overline grey--text text--darken-2 mb-2">Analysts</div>
              <div class="jira-field__row">
                <span class="jira-field__label">QA Analyst</span>
                <span v-if="!issue.resolvedAssignee" class="grey--text text--darken-2">—</span>
                <span v-else-if="issue.resolvedAssignee.found">{{ issue.resolvedAssignee.displayName }}</span>
                <span v-else class="d-inline-flex align-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon x-small color="warning" class="mr-1 flex-shrink-0" v-on="on" aria-hidden="true">fas fa-exclamation-triangle</v-icon>
                    </template>
                    <span>User could not be uniquely auto-mapped to a Continuum account</span>
                  </v-tooltip>
                  <span>{{ issue.resolvedAssignee.displayName }}</span>
                </span>
              </div>
              <div class="jira-field__row">
                <span class="jira-field__label">Build Analyst</span>
                <span v-if="!issue.resolvedReporter" class="grey--text text--darken-2">—</span>
                <span v-else-if="issue.resolvedReporter.found">{{ issue.resolvedReporter.displayName }}</span>
                <span v-else class="d-inline-flex align-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon x-small color="warning" class="mr-1 flex-shrink-0" v-on="on" aria-hidden="true">fas fa-exclamation-triangle</v-icon>
                    </template>
                    <span>User could not be uniquely auto-mapped to a Continuum account</span>
                  </v-tooltip>
                  <span>{{ issue.resolvedReporter.displayName }}</span>
                </span>
              </div>
              <div class="jira-field__row">
                <span class="jira-field__label">Support Analysts</span>
                <span v-if="!issue.resolvedWatchers || !issue.resolvedWatchers.length" class="grey--text text--darken-2">—</span>
                <span v-else>
                  <template v-for="(watcher, i) in issue.resolvedWatchers">
                    <span :key="i">
                      <span v-if="!watcher.found" class="d-inline-flex align-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon x-small color="warning" class="mr-1 flex-shrink-0" v-on="on" aria-hidden="true">fas fa-exclamation-triangle</v-icon>
                          </template>
                          <span>User could not be uniquely auto-mapped to a Continuum account</span>
                        </v-tooltip>
                        <span>{{ watcher.displayName }}</span>
                      </span>
                      <template v-else>{{ watcher.displayName }}</template>
                    </span>
                    <span v-if="i < issue.resolvedWatchers.length - 1" :key="`sep-${i}`">, </span>
                  </template>
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right panel: Confirm for Continuum (editable) -->
        <v-col cols="12" md="7" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="subtitle-1 font-weight-medium pb-1">Confirm for Continuum</v-card-title>
            <v-card-text class="flex-grow-1">
              <!-- Pack -->
              <div v-if="!isLoadingPacks && showPackEnvWarning && suggestedPackMatchFound" class="inline-alert inline-alert--info body-2 mb-2">
                Pack pre-filled from Jira, but this ID references a production environment — please confirm it's correct.
              </div>
              <div v-if="!isLoadingPacks && showPackEnvWarning && !suggestedPackMatchFound" class="inline-alert inline-alert--warning body-2 mb-2">
                The pack ID from Jira doesn't match any pack in this environment. Please select a pack manually.
              </div>
              <v-autocomplete
                :items="filteredPackList"
                :loading="isLoadingPacks"
                dense
                hide-details
                item-text="title"
                item-value="id"
                label="Pack *"
                outlined
                v-model="selectedPackId"
              ></v-autocomplete>

              <!-- Template Type -->
              <v-select
                :hint="templateTypeHint"
                :items="templateTypeOptions"
                class="mt-4"
                dense
                item-text="name"
                item-value="value"
                label="Template Type *"
                outlined
                persistent-hint
                v-model="selectedTemplateType"
              ></v-select>

              <!-- Application & Primary Activity -->
              <v-divider class="mt-5 mb-4"></v-divider>
              <div class="subtitle-2 mb-3">Application &amp; Primary Activity</div>
              <div v-if="isInitialized && suggestedApplicationId && suggestedPrimaryActivityId" class="inline-alert inline-alert--success body-2 mb-3">
                Both fields were pre-filled from Jira. Please verify before creating.
              </div>
              <div v-else-if="isInitialized && suggestedApplicationId" class="inline-alert inline-alert--warning body-2 mb-3">
                Application was pre-filled, but Primary Activity could not be auto-matched. Please select a Primary Activity manually.
              </div>
              <div v-else-if="isInitialized" class="inline-alert inline-alert--warning body-2 mb-3">
                Neither field could be auto-matched. Please select Application and Primary Activity manually.
              </div>
              <v-autocomplete
                :items="applicationItems"
                clearable
                dense
                hide-details
                item-text="label"
                item-value="id"
                label="Application *"
                outlined
                v-model="selectedApplicationId"
              ></v-autocomplete>
              <v-autocomplete
                :disabled="!selectedApplicationId"
                :items="primaryActivityItems"
                class="mt-4"
                clearable
                dense
                hide-details
                item-text="label"
                item-value="id"
                label="Primary Activity *"
                outlined
                v-model="selectedPrimaryActivityId"
              ></v-autocomplete>

              <!-- Service Area Attributes -->
              <v-divider class="mt-5 mb-4"></v-divider>
              <div class="subtitle-2 mb-3">Service Area Attributes</div>
              <div v-if="isInitialized && parsedJiraServiceAreaPairs.length && suggestedServiceAreaAttributeIds.length === parsedJiraServiceAreaPairs.length" class="inline-alert inline-alert--success body-2 mb-3">
                <template v-if="parsedJiraServiceAreaPairs.length === 1">The value was</template><template v-else>All {{ parsedJiraServiceAreaPairs.length }} values were</template> pre-filled from Jira. Please verify before creating.
              </div>
              <div v-else-if="isInitialized && parsedJiraServiceAreaPairs.length && suggestedServiceAreaAttributeIds.length" class="inline-alert inline-alert--warning body-2 mb-3">
                {{ suggestedServiceAreaAttributeIds.length }} of {{ parsedJiraServiceAreaPairs.length }} values were pre-filled; {{ parsedJiraServiceAreaPairs.length - suggestedServiceAreaAttributeIds.length }} could not be matched. Review and adjust manually.
              </div>
              <div v-else-if="isInitialized && parsedJiraServiceAreaPairs.length" class="inline-alert inline-alert--warning body-2 mb-3">
                Jira values could not be auto-matched. Select manually if applicable.
              </div>
              <v-autocomplete
                :item-text="serviceAreaAttributeDisplayText"
                :items="serviceAreaAttributeItems"
                class="mt-2"
                clearable
                dense
                hide-details
                item-value="id"
                label="Service Area Attributes"
                multiple
                outlined
                v-model="selectedServiceAreaAttributeIds"
              >
                <template #selection="{ item, parent }">
                  <v-chip small close class="ma-1" @click:close="parent.selectItem(item)">
                    <span class="service-area-chip-text">{{ serviceAreaAttributeDisplayText(item) }}</span>
                  </v-chip>
                </template>
              </v-autocomplete>
              <!-- User Roles -->
              <v-divider class="mt-5 mb-4"></v-divider>
              <div class="subtitle-2 mb-3">User Roles</div>
              <div v-if="isInitialized && jiraImpactedUserRolesRawValues.length && suggestedUserRoles.length === jiraImpactedUserRolesUniqueRawCount" class="inline-alert inline-alert--success body-2 mb-3">
                <template v-if="jiraImpactedUserRolesUniqueRawCount === 1">The value was</template><template v-else>All {{ jiraImpactedUserRolesUniqueRawCount }} values were</template> pre-filled from Jira. Please verify before creating.
              </div>
              <div v-else-if="isInitialized && jiraImpactedUserRolesRawValues.length && suggestedUserRoles.length" class="inline-alert inline-alert--warning body-2 mb-3">
                {{ suggestedUserRoles.length }} of {{ jiraImpactedUserRolesUniqueRawCount }} values were pre-filled; {{ jiraImpactedUserRolesUniqueRawCount - suggestedUserRoles.length }} could not be matched. Review and adjust manually.
              </div>
              <div v-else-if="isInitialized && jiraImpactedUserRolesRawValues.length" class="inline-alert inline-alert--warning body-2 mb-3">
                Jira values could not be auto-matched. Select manually if applicable.
              </div>
              <v-autocomplete
                :items="userRoleOptions"
                class="mt-2"
                clearable
                dense
                hide-details
                label="User Roles"
                multiple
                outlined
                v-model="selectedUserRoleValues"
              >
                <template #selection="{ item, parent }">
                  <v-chip small close class="ma-1" @click:close="parent.selectItem(item)">{{ item }}</v-chip>
                </template>
              </v-autocomplete>
            </v-card-text>
            <div class="action-bar">
              <v-btn @click="goToList" outlined class="mr-3" data-cy="jira-qa-import-create__cancel">Cancel</v-btn>
              <v-btn
                :disabled="!canSubmit"
                :loading="isSubmitting"
                @click="submit"
                color="primary"
                outlined
                data-cy="jira-qa-import-create__submit"
              >Create</v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "JiraQaImportCreate",

  data() {
    return {
      createdReleaseNote: null,
      isInitialized: false,
      isSubmitting: false,
      postCreateWarnings: [],
      selectedApplicationId: null,
      selectedPackId: null,
      selectedPrimaryActivityId: null,
      selectedServiceAreaAttributeIds: [],
      selectedTemplateType: null,
      selectedUserRoleValues: [],
      suggestedTemplateType: null,
      templateTypeReason: null,
    };
  },

  computed: {
    issue() {
      return this.$store.state.jiraQaImport.selectedIssue || {};
    },
    applicationItems() {
      const dropdown = this.adminDropdownList.find(d => d.attributeName === "applicationDropdownValueId");
      return dropdown ? dropdown.valueList.filter(v => !v.isDeprecated) : [];
    },
    primaryActivityItems() {
      if (!this.selectedApplicationId) return [];
      const dropdown = this.adminDropdownList.find(d => d.attributeName === "primaryActivityDropdownValueId");
      return dropdown ? dropdown.valueList.filter(v => !v.isDeprecated && v.groupDropdownValueId === this.selectedApplicationId) : [];
    },
    jiraServiceAreaValues() {
      const field = this.issue.fields?.customfield_18701;
      if (!field) return [];
      if (Array.isArray(field)) return field.map(f => f.value ?? f).filter(Boolean);
      const single = field.value ?? field;
      return single ? [String(single)] : [];
    },
    // Parses each raw Jira service area value into { categoryLabel, attributeLabel, raw }.
    // Handles "Category > Attribute" format (with any spacing around ">") and comma- or
    // semicolon-separated multi-value strings like "Cat1 > Attr1, Attr2; Cat2 > Attr3".
    // Values without ">" inherit the most recently seen category. Values without any
    // category context are treated as attribute-only (categoryLabel: null).
    parsedJiraServiceAreaPairs() {
      const pairs = [];
      for (const value of this.jiraServiceAreaValues) {
        // Track the most recently seen category so that subsequent comma- or semicolon-
        // separated attributes without a ">" inherit it.
        // e.g. "Impacted Applications > EpicCare AMB, Orders" → both belong to "Impacted Applications"
        // e.g. "Impacted Applications > PB, Hyperspace, TXT; Ochin Tracking > Communication Only"
        //   → PB/Hyperspace/TXT under "Impacted Applications", Communication Only under "Ochin Tracking"
        let currentCategory = null;
        for (const segment of value.split(/[,;]/)) {
          const trimmed = segment.trim();
          if (!trimmed) continue;
          const gtIndex = trimmed.indexOf(">");
          if (gtIndex !== -1) {
            currentCategory = trimmed.slice(0, gtIndex).trim();
            const attributeLabel = trimmed.slice(gtIndex + 1).trim();
            if (attributeLabel) pairs.push({ categoryLabel: currentCategory, attributeLabel, raw: trimmed });
          } else {
            // No ">": additional attribute under the most recently seen category (may be null).
            pairs.push({ categoryLabel: currentCategory, attributeLabel: trimmed, raw: trimmed });
          }
        }
      }
      return pairs;
    },
    jiraServiceAreaLabel() {
      return this.parsedJiraServiceAreaPairs.map(p => p.raw).join(", ") || null;
    },
    serviceAreaAttributeItems() {
      return this.serviceAreaAttributeList
        .filter(v => !v.isDeprecated)
        .sort((a, b) => {
          const catA = this.serviceAreaCategoryList.find(c => c.id === a.categoryId)?.name ?? "";
          const catB = this.serviceAreaCategoryList.find(c => c.id === b.categoryId)?.name ?? "";
          return catA.localeCompare(catB) || a.name.localeCompare(b.name);
        });
    },
    suggestedServiceAreaAttributeIds() {
      const acc = [];
      for (const { categoryLabel, attributeLabel } of this.parsedJiraServiceAreaPairs) {
        // Narrow to the matched category when possible; fall back to all attributes.
        const matchedCategory = this.findServiceAreaCategory(categoryLabel);
        const candidates = matchedCategory
          ? this.serviceAreaAttributeItems.filter(v => v.categoryId === matchedCategory.id)
          : this.serviceAreaAttributeItems;
        const matched = this.findServiceAreaAttribute(candidates, attributeLabel);
        if (matched && !acc.includes(matched.id)) acc.push(matched.id);
      }
      return acc;
    },
    userRoleOptions() {
      return this.$store.getters["dropdown/getOptionList"]("userRoles") || [];
    },
    jiraImpactedUserRolesRawValues() {
      const field = this.issue.fields?.customfield_12309;
      if (!field) return [];
      if (Array.isArray(field)) return field.map(f => { const v = f.value ?? f; return v != null ? String(v).trim() : null; }).filter(Boolean);
      if (typeof field === "string") return field.split(",").map(s => s.trim()).filter(Boolean);
      const single = field.value ?? field;
      const normalized = single != null ? String(single).trim() : "";
      return normalized ? [normalized] : [];
    },
    jiraImpactedUserRolesLabel() {
      return this.jiraImpactedUserRolesRawValues.join(", ") || null;
    },
    jiraImpactedUserRolesUniqueRawCount() {
      return new Set(this.jiraImpactedUserRolesRawValues.map(v => this.stripAcronym(v.toLowerCase().trim()))).size;
    },
    suggestedUserRoles() {
      const seen = new Set();
      return this.jiraImpactedUserRolesRawValues
        .map(raw => {
          const lower = raw.toLowerCase();
          const exact = this.userRoleOptions.find(r => r.toLowerCase() === lower);
          if (exact) return exact;
          const stripped = this.stripAcronym(lower);
          return this.userRoleOptions.find(r => this.stripAcronym(r).toLowerCase() === stripped) ?? null;
        })
        .filter(r => {
          if (!r || seen.has(r)) return false;
          seen.add(r);
          return true;
        });
    },
    jiraApplicationLabel() {
      const jiraApp = this.issue.fields?.customfield_18600;
      if (!Array.isArray(jiraApp) || !jiraApp.length) return null;
      return jiraApp.map(a => a.value ?? a).filter(Boolean).join(", ");
    },
    jiraPrimaryActivityLabel() {
      const jiraActivity = this.issue.fields?.customfield_14802;
      if (!jiraActivity) return null;
      return jiraActivity.value ?? jiraActivity ?? null;
    },
    suggestedApplicationId() {
      const jiraApp = this.issue.fields?.customfield_18600;
      if (!Array.isArray(jiraApp) || !jiraApp.length) return null;
      // Best-effort: match on the first Jira application value only. Multi-value
      // Jira application fields are uncommon; additional values are ignored here.
      const jiraLabel = (jiraApp[0].value ?? jiraApp[0] ?? "").toLowerCase();
      const dropdown = this.adminDropdownList.find(d => d.attributeName === "applicationDropdownValueId");
      if (!dropdown) return null;
      const exactMatch = dropdown.valueList.find(v => !v.isDeprecated && v.label.toLowerCase() === jiraLabel);
      if (exactMatch) return exactMatch.id;
      const strippedJiraLabel = this.stripAcronym(jiraLabel);
      return dropdown.valueList.find(v => !v.isDeprecated && this.stripAcronym(v.label).toLowerCase() === strippedJiraLabel)?.id ?? null;
    },
    suggestedPrimaryActivityId() {
      if (!this.selectedApplicationId) return null;
      const jiraActivity = this.issue.fields?.customfield_14802;
      if (!jiraActivity) return null;
      const jiraLabel = (jiraActivity.value ?? jiraActivity ?? "").toLowerCase();
      const dropdown = this.adminDropdownList.find(d => d.attributeName === "primaryActivityDropdownValueId");
      if (!dropdown) return null;
      const exactMatch = dropdown.valueList.find(v =>
        !v.isDeprecated &&
        v.groupDropdownValueId === this.selectedApplicationId &&
        v.label.toLowerCase() === jiraLabel
      );
      if (exactMatch) return exactMatch.id;
      const strippedJiraLabel = this.stripAcronym(jiraLabel);
      return dropdown.valueList.find(v =>
        !v.isDeprecated &&
        v.groupDropdownValueId === this.selectedApplicationId &&
        this.stripAcronym(v.label).toLowerCase() === strippedJiraLabel
      )?.id ?? null;
    },
    templateTypeHint() {
      if (this.suggestedTemplateType && this.selectedTemplateType !== this.suggestedTemplateType) {
        const suggestedName = this.templateTypeOptions.find(o => o.value === this.suggestedTemplateType)?.name ?? this.suggestedTemplateType;
        return `Jira suggested ${suggestedName} — you've selected something different. Double-check this is correct.`;
      }
      if (this.templateTypeReason === "communicationOnly") {
        return "Set to Communication Only based on Service Area Attributes — overrides Build Type and Release Type";
      }
      if (this.templateTypeReason === "releaseType") {
        return "Set to Optional Enhancement based on Release Type — verify before creating";
      }
      if (this.templateTypeReason === "buildType") {
        return "Pre-filled from Build Type — verify before creating";
      }
      return "No Build Type match — defaulted to All";
    },
    unmappedWatchers() {
      return (this.issue.resolvedWatchers || []).filter(w => !w.found);
    },
    hasUnmappedFields() {
      return (
        (this.issue.resolvedAssignee && !this.issue.resolvedAssignee.found) ||
        (this.issue.resolvedReporter && !this.issue.resolvedReporter.found) ||
        this.unmappedWatchers.length > 0
      );
    },
    hasMultipleUnmapped() {
      const count = [
        this.issue.resolvedAssignee && !this.issue.resolvedAssignee.found,
        this.issue.resolvedReporter && !this.issue.resolvedReporter.found,
        this.unmappedWatchers.length > 0,
      ].filter(Boolean).length;
      return count > 1 || this.unmappedWatchers.length > 1;
    },
    canSubmit() {
      const packValid = this.filteredPackList.some(p => p.id === this.selectedPackId);
      return packValid && !!this.selectedTemplateType && !!this.selectedApplicationId && !!this.selectedPrimaryActivityId;
    },
    filteredPackList() {
      return this.packList.filter(pack => {
        if (!pack.pilotParticipationList || !pack.pilotParticipationList.length || this.isOchinUser) return true;
        return pack.pilotParticipationList.includes(this.loggedInUserServiceArea);
      });
    },
    isLoadingPacks() {
      return !this.packList.length;
    },
    templateTypeOptions() {
      const templates = this.$store.state.templates.list;
      return Object.keys(templates).map(key => ({ value: key, name: templates[key].name }));
    },
    isProduction() {
      const env = this.$store.state.application.env;
      return !env || env === "production";
    },
    suggestedPackMatchFound() {
      if (!this.issue.suggestedPackId) return false;
      return this.filteredPackList.some(pack => pack.id === this.issue.suggestedPackId);
    },
    showPackEnvWarning() {
      return !this.isProduction && !!this.issue.suggestedPackId;
    },
    ...mapState({
      adminDropdownList: state => state.admin.dropdown.list,
      isOchinUser: state => state.session.user.isOchinUser,
      loggedInUserServiceArea: state => state.session.user.serviceArea,
      packList: state => state.pack.packList,
      serviceAreaAttributeList: state => state.admin.serviceAreaAttribute.list,
      serviceAreaCategoryList: state => state.admin.serviceAreaCategory.list,
    }),
  },

  async created() {
    this.selectedPackId = this.issue.suggestedPackId || null;
    const buildTypeValue = this.issue.fields?.customfield_12314?.value ?? this.issue.fields?.customfield_12314;
    const releaseTypeValue = this.issue.fields?.customfield_12315?.value ?? this.issue.fields?.customfield_12315;
    const TEMPLATE_BY_BUILD_TYPE = { New: "newBuild", Update: "updateBuild", Fix: "breakFix" };
    const buildTypeTemplate = TEMPLATE_BY_BUILD_TYPE[buildTypeValue] ?? "all";

    // Check if Release Type is Optional - set to optionalEnhancement
    const isOptionalReleaseType = releaseTypeValue === "Optional - Jira Needed" || releaseTypeValue === "Optional - No Jira Needed";

    // Early Communication Only check on the parsed attribute label (handles "Category > Communication Only"
    // and plain "Communication Only" before the async data load). The late check below handles
    // cases where the attribute name matches only via acronym stripping.
    const earlyIsCommunicationOnly = this.parsedJiraServiceAreaPairs.some(
      p => p.attributeLabel.toLowerCase() === "communication only"
    );
    if (earlyIsCommunicationOnly) {
      this.selectedTemplateType = "communicationOnly";
      this.suggestedTemplateType = "communicationOnly";
      this.templateTypeReason = "communicationOnly";
    } else if (isOptionalReleaseType) {
      this.selectedTemplateType = "optionalEnhancement";
      this.suggestedTemplateType = "optionalEnhancement";
      this.templateTypeReason = "releaseType";
    } else {
      this.selectedTemplateType = buildTypeTemplate;
      this.suggestedTemplateType = buildTypeTemplate;
      this.templateTypeReason = buildTypeTemplate !== "all" ? "buildType" : "default";
    }

    try {
      await Promise.all([
        this.getPackList(),
        this.fetchAdminDropdownList(),
        this.fetchServiceAreaAttributeList(),
        this.fetchServiceAreaCategoryList(),
      ]);
      this.selectedApplicationId = this.suggestedApplicationId;
      this.selectedPrimaryActivityId = this.suggestedPrimaryActivityId;
      this.selectedServiceAreaAttributeIds = this.suggestedServiceAreaAttributeIds;
      this.selectedUserRoleValues = this.suggestedUserRoles;

      // Late Communication Only check: catches cases where the Jira value matched a Continuum
      // "Communication Only" attribute via acronym stripping (e.g. "Communication Only (CO)").
      // Runs after the attribute load so suggestedServiceAreaAttributeIds is populated.
      // communicationOnly always takes precedence over Build Type and Release Type defaults.
      const lateIsCommunicationOnly = this.selectedServiceAreaAttributeIds.some(id =>
        this.serviceAreaAttributeItems.find(a => a.id === id)?.name.toLowerCase() === "communication only"
      );
      if (lateIsCommunicationOnly) {
        this.selectedTemplateType = "communicationOnly";
        this.suggestedTemplateType = "communicationOnly";
        this.templateTypeReason = "communicationOnly";
      }
    } catch (e) {
      this.$notifyError({ message: "Could not load data required to create a release note." });
      this.$router.push({ name: "jiraQaImport" });
      return;
    }

    this.isInitialized = true;
  },

  beforeDestroy() {
    this.$store.commit("jiraQaImport/clearSelectedIssue");
  },

  watch: {
    selectedApplicationId(newVal, oldVal) {
      if (newVal !== oldVal && oldVal !== null) {
        this.selectedPrimaryActivityId = null;
      }
    },
  },

  methods: {
    singleSelectValue(field) {
      if (!field) return "";
      return field.value || field;
    },
    multiSelectValue(field) {
      if (!field) return "";
      if (Array.isArray(field)) {
        return field.map(item => item?.value ?? item).filter(Boolean).join(", ");
      }
      if (typeof field === "object") {
        return field.value || "";
      }
      return typeof field === "string" ? field : "";
    },
    stripAcronym(label) {
      return label.replace(/\s*\([^)]+\)\s*$/, "").trim();
    },
    // Find a Continuum service area category by name (case-insensitive, acronym-tolerant).
    findServiceAreaCategory(label) {
      if (!label) return null;
      const lower = label.toLowerCase();
      const exact = this.serviceAreaCategoryList.find(c => c.name.toLowerCase() === lower);
      if (exact) return exact;
      const stripped = this.stripAcronym(lower);
      return this.serviceAreaCategoryList.find(c => this.stripAcronym(c.name).toLowerCase() === stripped) ?? null;
    },
    // Find a Continuum service area attribute by name within a candidate list
    // (case-insensitive, acronym-tolerant).
    findServiceAreaAttribute(candidates, label) {
      if (!label) return null;
      const lower = label.toLowerCase();
      const exact = candidates.find(v => v.name.toLowerCase() === lower);
      if (exact) return exact;
      const stripped = this.stripAcronym(lower);
      return candidates.find(v => this.stripAcronym(v.name).toLowerCase() === stripped) ?? null;
    },
    goToList() {
      this.$router.push({ name: "jiraQaImport" });
    },
    goToReleaseNote() {
      const rn = this.createdReleaseNote;
      this.$router.push({ name: "releaseNoteView", params: { packId: rn.packId, releaseNoteId: rn.id } });
    },
    async submit() {
      this.isSubmitting = true;
      try {
        const { result, warnings } = await this.$store.dispatch("jiraQaImport/createReleaseNote", {
          issueKey: this.issue.key,
          packId: this.selectedPackId,
          templateType: this.selectedTemplateType,
          applicationDropdownValueId: this.selectedApplicationId || undefined,
          primaryActivityDropdownValueId: this.selectedPrimaryActivityId || undefined,
          serviceAreaAttributeList: this.selectedServiceAreaAttributeIds.length ? this.selectedServiceAreaAttributeIds : undefined,
          userRole: this.selectedUserRoleValues.length ? this.selectedUserRoleValues : undefined,
        });
        this.createdReleaseNote = result;
        this.postCreateWarnings = warnings || [];
      } catch (e) {
        this.$notifyError({ message: `Could not create release note for ${this.issue.key}` });
      } finally {
        this.isSubmitting = false;
      }
    },
    serviceAreaAttributeDisplayText(item) {
      if (!item) return "";
      const category = this.serviceAreaCategoryList.find(c => c.id === item.categoryId);
      return category ? `${category.name} - ${item.name}` : item.name;
    },
    ...mapActions({
      fetchAdminDropdownList: "admin/dropdown/list",
      fetchServiceAreaAttributeList: "admin/serviceAreaAttribute/list",
      fetchServiceAreaCategoryList: "admin/serviceAreaCategory/list",
      getPackList: "pack/getList",
    }),
  },
};
</script>

<style scoped>
.jira-field__label {
  display: block;
  font-size: 0.75rem;
  color: #616161;
  margin-bottom: 2px;
}
.jira-field__row {
  margin-bottom: 10px;
}
::v-deep .v-alert__icon {
  align-self: center;
}
.inline-alert {
  padding: 8px 12px;
  border-radius: 4px;
  border-left-width: 3px;
  border-left-style: solid;
  color: rgba(0, 0, 0, 0.87);
}
.inline-alert--success {
  border-left-color: #4caf50;
  background-color: #f1f8f1;
}
.inline-alert--warning {
  border-left-color: #fb8c00;
  background-color: #fffaf5;
}
.inline-alert--info {
  border-left-color: #2196f3;
  background-color: #f0f7ff;
}
.success-banner {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #f1f8f1;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.manual-assignment-section {
  border-left: 3px solid #fb8c00;
  background-color: #fffaf5;
  border-radius: 0 4px 4px 0;
}
.service-area-chip-text {
  display: block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.action-bar {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  z-index: 1;
}
</style>
